/* eslint-disable no-console */
import { withTheme } from '@digital-retail/falabella-ui-cross-components';
import { getMaxQuantityString, getQuantityMapFromStorage, isTottusStandalone } from '@digital-retail/store-manager';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { withCart } from '../../utils/CartContext';
import { capitalizeString } from '../../utils/jsUtils';
import _ from '../../utils/LodashImports';
import { countDecimalPlaces } from '../../utils/PriceHelpers';
import { withProduct } from '../../utils/ProductContext';
import { useUser } from '../../utils/UserContext';
import { getMeasurements } from '../../utils/variant';
import { getCartItem } from '../Cart/CartItem/CartItem.util';
import Button from '../ui/Button/Button';
import { A2CPersistContainerStyles } from './AddToCartPersistContainer.style';

const AddToCartPersistContainer = ({
  isAddToCartInProgress = false,
  labels,
  handleAddToCart,
  getATCButtonText,
  inputVal,
  maxCartQuantity,
  handleRemoveFromCart,
  isRemoveFromCartInProgress,
  product,
  theme,
  cart,
  selectedSize,
  sizes,
  store,
}) => {
  const { currentVariant: currentVariantId } = product;
  const [skuCartCount, setSkuCartCount] = useState(0);
  const [cartLineId, setCartLineId] = useState('');
  const styles = A2CPersistContainerStyles(theme);
  const { tick } = useUser();
  const [maxCartQuantityLimit, setMaxCartQuantityLimit] = useState(0);
  const isTottusSA = isTottusStandalone(store);

  const cartItem = getCartItem({
    skuId: currentVariantId,
    cartItems: cart.cartItems,
  });

  const measurement = getMeasurements({ product });
  const unitOfMeasurement = measurement?.['unidad-de-medida'] || '';
  const minSaleQty = measurement?.skuminsaleqty ?? 1;

  const getMaxCartCount = (maxQuantity, minQuantity) => {
    const baseMax = maxQuantity / minQuantity;
    // eslint-disable-next-line no-nested-ternary
    return isTottusSA ? (maxCartQuantityLimit !== 0 ? maxCartQuantityLimit : baseMax) : baseMax;
  };
  const maxCartCount = getMaxCartCount(maxCartQuantity, minSaleQty);

  const maxQuantityText = _.get(labels, 'MAX_QUANTITY_TEXT_PDP', 'Máximo {maxCartQuantity} {unitOfMeasurement}.')
    .replace('{maxCartQuantity}', maxCartCount)
    .replace('{unitOfMeasurement}', capitalizeString(unitOfMeasurement) || 'unidades');

  const noSizeSelected = sizes.length > 0 && !selectedSize;

  useEffect(() => {
    const quantitiesMap = getQuantityMapFromStorage();
    const cartValue = quantitiesMap.get(currentVariantId) || {};
    if (cartItem) {
      setCartLineId(cartItem.cartLineId);
      setSkuCartCount(cartItem.totalQuantity);
      if (cartItem.skuId === currentVariantId && cartItem.qtyLimits) {
        setMaxCartQuantityLimit(cartItem.qtyLimits);
      } else if (cartValue.quantityLimit) {
        setMaxCartQuantityLimit(cartValue.quantityLimit);
      }
      return;
    }
    setSkuCartCount(cartValue.quantity || 0);
    setCartLineId(cartValue.cartLineId || '');
    if (cartValue.quantityLimit) {
      setMaxCartQuantityLimit(cartValue.quantityLimit);
    }
  }, [cartItem, currentVariantId, cart.cartItems, tick]);

  const getSkuQuantity = () => {
    const decimalPlaces = countDecimalPlaces(minSaleQty);
    const quantity = Number(minSaleQty * skuCartCount);
    return quantity.toFixed(decimalPlaces);
  };

  const handleCartAction = useCallback(
    (actionType) => {
      if (isAddToCartInProgress || isRemoveFromCartInProgress) {
        return;
      }

      if (actionType === 'add') {
        if (skuCartCount >= maxCartCount) {
          return;
        }
        handleAddToCart(inputVal, skuCartCount, cartLineId);
      } else if (actionType === 'remove') {
        handleRemoveFromCart({
          totalQuantity: skuCartCount,
          cartLineId,
          quantity: 1,
        });
      }
    },
    // We need to redefine when product changes so we use the currently selected variant.
    // eslint-disable-next-line
    [
      isAddToCartInProgress,
      isRemoveFromCartInProgress,
      skuCartCount,
      maxCartCount,
      handleAddToCart,
      inputVal,
      handleRemoveFromCart,
      cartLineId,
      product,
    ]
  );

  const renderCartActionContainer = () => (
    <>
      <div className={`cart-persist ${isAddToCartInProgress || isRemoveFromCartInProgress ? 'disabled' : ''}`}>
        <button className="remove-from-cart-button" type="submit" onClick={() => handleCartAction('remove')}>
          <span className={`remove-from-cart-icon ${skuCartCount === 1 ? 'trash' : 'minus'}`} />
        </button>
        <div className="count-from-cart">
          {getSkuQuantity()} {unitOfMeasurement.toLowerCase()}
        </div>
        <button
          type="submit"
          className={`add-to-cart-button ${skuCartCount >= maxCartCount ? 'disabled' : ''}`}
          onClick={() => handleCartAction('add')}
        >
          <span className="add-to-cart-icon" />
        </button>
      </div>
      <div className="max-units-text">
        {maxCartQuantity && (
          <p>
            {isTottusSA
              ? getMaxQuantityString({ maxUnits: maxCartCount * minSaleQty, unit: unitOfMeasurement })
              : maxQuantityText}
          </p>
        )}
      </div>
      <style jsx>{styles}</style>
    </>
  );

  return (
    <div className="persist-container">
      {skuCartCount > 0 && (!isTottusStandalone || (isTottusStandalone && !noSizeSelected)) ? (
        renderCartActionContainer()
      ) : (
        <Button
          key="add-to-cart-button"
          disabled={isAddToCartInProgress || isRemoveFromCartInProgress}
          size="xtra-large"
          id="add-to-cart-button"
          type="mkp-primary"
          onClick={() => handleCartAction('add')}
        >
          {isAddToCartInProgress ? labels.ADDING_TO_CART : getATCButtonText()}
        </Button>
      )}

      <style jsx>{styles}</style>
    </div>
  );
};

AddToCartPersistContainer.defaultProps = {
  isAddToCartInProgress: false,
  maxCartQuantity: 999,
  product: {},
  cart: {},
  isRemoveFromCartInProgress: false,
  selectedSize: null,
  sizes: [],
};

AddToCartPersistContainer.propTypes = {
  isAddToCartInProgress: PropTypes.bool,
  labels: PropTypes.object.isRequired,
  handleAddToCart: PropTypes.func.isRequired,
  getATCButtonText: PropTypes.string.isRequired,
  inputVal: PropTypes.number.isRequired,
  maxCartQuantity: PropTypes.number,
  handleRemoveFromCart: PropTypes.func.isRequired,
  isRemoveFromCartInProgress: PropTypes.bool,
  theme: PropTypes.object.isRequired,
  cart: PropTypes.object,
  product: PropTypes.object,
  selectedSize: PropTypes.string,
  sizes: PropTypes.arrayOf(PropTypes.string),
  store: PropTypes.string.isRequired,
};

export default withTheme(withCart(withProduct(AddToCartPersistContainer)));
export { AddToCartPersistContainer };
